import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  CheckCircle, XCircle, ArrowLeft, Award, Target, Clock, FileText,
  HelpCircle, ChevronDown, ChevronUp
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExamResultDetail = () => {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());

  useEffect(() => {
    if (!isAuthenticated) {
      toast.error('Please login to view results');
      navigate('/auth/login');
      return;
    }
    fetchResult();
  }, [attemptId, isAuthenticated]);

  const fetchResult = async () => {
    try {
      const res = await axios.get(`${API}/student/exams/results/${attemptId}`);
      setData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load result');
      navigate('/student/exams');
    } finally {
      setLoading(false);
    }
  };

  const toggleQuestion = (index) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!data) return null;

  const { attempt, exam, review } = data;
  const passed = attempt.result === 'pass';

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <div className="max-w-4xl mx-auto py-8 px-4">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-4"
          onClick={() => navigate('/student/exams')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Exams
        </Button>

        {/* Result Summary Card */}
        <div className={`rounded-2xl shadow-lg p-8 mb-8 ${
          passed ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-red-500 to-rose-600'
        } text-white`} data-testid="result-summary">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-2">{exam?.title}</h1>
              <p className="text-white/80">{exam?.course_name}</p>
            </div>
            <div className="text-center">
              <div className={`w-24 h-24 rounded-full flex items-center justify-center ${
                passed ? 'bg-white/20' : 'bg-white/20'
              }`}>
                {passed ? (
                  <Award className="h-12 w-12 text-white" />
                ) : (
                  <XCircle className="h-12 w-12 text-white" />
                )}
              </div>
              <p className="mt-2 font-bold text-lg">
                {passed ? 'PASSED' : 'FAILED'}
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-4 gap-4 mt-8">
            <div className="bg-white/20 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold">{attempt.marks_obtained}</p>
              <p className="text-sm text-white/80">Marks</p>
            </div>
            <div className="bg-white/20 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold">{attempt.percentage?.toFixed(1)}%</p>
              <p className="text-sm text-white/80">Percentage</p>
            </div>
            <div className="bg-white/20 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold">{attempt.correct_answers}</p>
              <p className="text-sm text-white/80">Correct</p>
            </div>
            <div className="bg-white/20 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold">{attempt.wrong_answers}</p>
              <p className="text-sm text-white/80">Wrong</p>
            </div>
          </div>
        </div>

        {/* Questions Review */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold text-slate-800 mb-6 flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-blue-600" />
            Answer Review
          </h2>
          
          <div className="space-y-4">
            {review?.map((q, idx) => (
              <div 
                key={idx} 
                className={`border rounded-lg overflow-hidden ${
                  q.is_correct ? 'border-green-200' : 'border-red-200'
                }`}
                data-testid={`review-question-${idx}`}
              >
                <button
                  onClick={() => toggleQuestion(idx)}
                  className={`w-full px-4 py-3 flex items-center justify-between ${
                    q.is_correct ? 'bg-green-50' : 'bg-red-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {q.is_correct ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600" />
                    )}
                    <span className="font-medium text-slate-800">
                      Q{idx + 1}. {q.question.slice(0, 50)}...
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${
                      q.is_correct ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {q.is_correct ? `+${q.marks}` : '0'} marks
                    </span>
                    {expandedQuestions.has(idx) ? (
                      <ChevronUp className="h-5 w-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-slate-400" />
                    )}
                  </div>
                </button>
                
                {expandedQuestions.has(idx) && (
                  <div className="px-4 py-4 border-t bg-white">
                    <p className="font-medium text-slate-800 mb-4">{q.question}</p>
                    
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {Object.entries(q.options).map(([key, value]) => {
                        const isCorrect = key === q.correct_option;
                        const isSelected = key === q.student_answer;
                        
                        return (
                          <div 
                            key={key}
                            className={`p-3 rounded-lg border ${
                              isCorrect 
                                ? 'bg-green-50 border-green-300' 
                                : isSelected 
                                  ? 'bg-red-50 border-red-300' 
                                  : 'bg-slate-50 border-slate-200'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                                isCorrect 
                                  ? 'bg-green-500 text-white' 
                                  : isSelected 
                                    ? 'bg-red-500 text-white' 
                                    : 'bg-slate-300 text-slate-600'
                              }`}>
                                {key}
                              </span>
                              <span className="flex-1">{value}</span>
                              {isCorrect && <CheckCircle className="h-4 w-4 text-green-600" />}
                              {isSelected && !isCorrect && <XCircle className="h-4 w-4 text-red-600" />}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="text-sm">
                      <p className="text-slate-600">
                        <span className="font-medium">Your Answer:</span>{' '}
                        <span className={q.is_correct ? 'text-green-600' : 'text-red-600'}>
                          {q.student_answer === 'Not answered' ? 'Not answered' : `Option ${q.student_answer}`}
                        </span>
                      </p>
                      <p className="text-slate-600">
                        <span className="font-medium">Correct Answer:</span>{' '}
                        <span className="text-green-600">Option {q.correct_option}</span>
                      </p>
                      {q.explanation && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                          <p className="text-blue-800">
                            <span className="font-medium">Explanation:</span> {q.explanation}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ExamResultDetail;
